import { Entity, Id, VEHICLE_BRAND_LABELS } from './common.model';

/**
 * Datos de una persona que Reservas puede pedir que se corrijan: los que se ven en su pantalla de
 * inscritos y los que hacen falta para hoteles, ferrys y fronteras.
 *
 * El email queda fuera a propósito: al cambiarlo, el backend busca esa dirección en Auth y, si no
 * existe, crea una cuenta nueva y reasigna a la persona dentro de la inscripción. Eso no puede
 * salir de una solicitud; se hace a mano desde la ficha del equipo.
 */
export const PARTICIPANT_REQUESTABLE_FIELDS = [
  'fullName',
  'birthday',
  'nationality',
  'documentNumber',
  'passport',
  'passportExpiration',
  'phone',
] as const;

export type ParticipantRequestableField = (typeof PARTICIPANT_REQUESTABLE_FIELDS)[number];

/** Datos del equipo y del coche, que son de la inscripción entera y no de una persona. */
export const TEAM_REQUESTABLE_FIELDS = [
  'teamName',
  'vehicleType',
  'vehicleBrand',
  'otherVehicleBrand',
  'vehicleModel',
  'vehicleYear',
  'vehicleCC',
  'vehicleCV',
  'vehiclePlateNumber',
  'vehicleGps',
] as const;

export type TeamRequestableField = (typeof TEAM_REQUESTABLE_FIELDS)[number];

export type RequestableField = ParticipantRequestableField | TeamRequestableField;

/** Cómo se llama cada dato en pantalla. */
export const REQUESTABLE_FIELD_LABELS: Record<RequestableField, string> = {
  fullName: 'Nombre completo',
  birthday: 'Fecha de nacimiento',
  nationality: 'Nacionalidad',
  documentNumber: 'DNI / Documento',
  passport: 'Pasaporte',
  passportExpiration: 'Caducidad del pasaporte',
  phone: 'Teléfono',
  teamName: 'Nombre del equipo',
  vehicleType: 'Tipo de vehículo',
  vehicleBrand: 'Marca',
  otherVehicleBrand: 'Marca (si no está en la lista)',
  vehicleModel: 'Modelo',
  vehicleYear: 'Año del vehículo',
  vehicleCC: 'Cilindrada',
  vehicleCV: 'Potencia (CV)',
  vehiclePlateNumber: 'Matrícula',
  vehicleGps: 'GPS',
};

/** Tipos de vehículo que admite la ficha de inscripción. */
export const VEHICLE_TYPE_OPTIONS = ['4x2', '4x4'];

/** A quién afecta el dato: a la persona o al equipo y su coche. */
export type ChangeRequestScope = 'participant' | 'team';

export type ParticipantSlot = 'pilot' | 'copilot' | 'participant';

export type ChangeRequestStatus = 'PENDING' | 'APPLIED' | 'REJECTED' | 'CANCELLED';

export const CHANGE_REQUEST_STATUS_LABELS: Record<ChangeRequestStatus, string> = {
  PENDING: 'Pendiente',
  APPLIED: 'Aplicada',
  REJECTED: 'Rechazada',
  CANCELLED: 'Anulada',
};

/** Un dato concreto que se pide cambiar, con lo que había y lo que debería poner. */
export interface ChangeRequestField {
  scope: ChangeRequestScope;
  field: RequestableField;
  /** El valor que tenía la ficha cuando se pidió el cambio. */
  currentValue: string;
  /** El valor correcto, según quien lo pide. */
  proposedValue: string;
}

/**
 * Una petición de Reservas para que Inscripciones corrija la ficha de un equipo. Reservas ve las
 * fichas pero no puede tocarlas, así que deja aquí lo que hay que cambiar —datos de una persona,
 * del coche, o de las dos cosas— y quien lleva las inscripciones lo aplica de una vez.
 *
 * El equipo, el dorsal y el nombre se copian tal y como estaban al pedirlo: así la lista se lee
 * entera sin abrir cada inscripción, aunque luego cambien.
 */
export interface InscriptionChangeRequest extends Entity {
  editionId: Id;
  inscriptionId: Id;
  /** Desde qué persona se abrió la solicitud, aunque solo pida datos del coche. */
  participantSlot: ParticipantSlot;
  /** Posición dentro de la lista de acompañantes; solo cuando el participante es uno de ellos. */
  participantIndex: number | null;
  /** Identificador de la persona, que es lo que no cambia aunque se reordene el equipo. */
  participantId: Id | null;
  participantName: string;
  /** Piloto, Copiloto 1, Copiloto 2… tal y como se ve en la tabla de Reservas. */
  participantRoleLabel: string;
  teamName: string;
  dorsal: string;
  changes: ChangeRequestField[];
  /** Aclaración opcional de quien pide el cambio. */
  note: string;
  status: ChangeRequestStatus;
  requestedBy: Id;
  requestedByEmail: string;
  requestedAt: Date;
  resolvedBy?: Id;
  resolvedByEmail?: string;
  resolvedAt?: Date;
  /** Por qué se rechazó, o lo que quiera dejar dicho quien la cierra. */
  resolutionNote?: string;
}

/**
 * Un dato solo se puede pedir —y aplicar— si está en las listas de arriba. Lo comprueba también
 * quien aplica la solicitud: las reglas no miran qué campos lleva dentro, así que sin esto una
 * solicitud manipulada podría acabar escribiendo cualquier cosa en la inscripción.
 */
export function isRequestableField(scope: ChangeRequestScope, field: string): boolean {
  const allowed: readonly string[] = scope === 'team' ? TEAM_REQUESTABLE_FIELDS : PARTICIPANT_REQUESTABLE_FIELDS;
  return allowed.includes(field);
}

/** Las marcas se guardan en clave (LAND_ROVER) pero en pantalla se leen como toca. */
export function requestValueLabel(field: RequestableField, value: string): string {
  if (!value) return '';
  if (field === 'vehicleBrand') return VEHICLE_BRAND_LABELS[value] ?? value;
  return value;
}
